import React, { useState, useRef } from "react";
import { useSupport } from "../context/SupportContext";
import { useUploader } from "@utils";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";
import { Button } from "@shadcn-in-nx/ui/button";
import { Input } from "@shadcn-in-nx/ui/input";
import { Label } from "@shadcn-in-nx/ui/label";
import { Textarea } from "@shadcn-in-nx/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@shadcn-in-nx/ui/select";
import {
    Loader2,
    MessageSquarePlus,
    Paperclip,
    X,
    File,
    Image as ImageIcon,
    Film,
    FileText,
    AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

// Helper to get file icon based on type
const getFileIcon = (type) => {
    if (type?.startsWith("image/")) return ImageIcon;
    if (type?.startsWith("video/")) return Film;
    if (type?.includes("pdf")) return FileText;
    return File;
};

export default function CreateTicketModal({ open, onOpenChange, onSuccess }) {
    const { t } = useTranslation();
    const { createTicket, isCreatingTicket } = useSupport();

    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [priority, setPriority] = useState("NORMAL");
    const fileInputRef = useRef(null);

    // File uploader
    const {
        loading: isUploading,
        files: uploadedFiles,
        uploadFiles,
        removeFile,
        clearFiles,
    } = useUploader({ isPublic: true });

    const handleFileSelect = (e) => {
        const files = e.target.files;
        if (files?.length) {
            uploadFiles(files);
        }
        // Reset input
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!subject.trim() || !message.trim()) {
            toast.error(t("support.createTicket.fillRequired"));
            return;
        }

        try {
            // Format attachments from uploaded files
            const attachments = uploadedFiles
                .filter(f => f.status === "completed")
                .map(f => ({
                    url: f.permanentUrl || f.url,
                    name: f.name,
                    type: f.file?.type,
                    size: f.file?.size,
                }));

            const response = await createTicket(subject.trim(), message.trim(), priority, attachments);
            if (response?.data) {
                toast.success(t("support.createTicket.success"));
                // Reset form
                setSubject("");
                setMessage("");
                setPriority("NORMAL");
                clearFiles();
                onOpenChange(false);
                // Call onSuccess with the new ticket ID if provided
                if (onSuccess && response.data.ticketId) {
                    onSuccess(response.data.ticketId);
                }
            }
        } catch (error) {
            toast.error(t("support.createTicket.failed"));
        }
    };

    const handleCancel = () => {
        setSubject("");
        setMessage("");
        setPriority("NORMAL");
        clearFiles();
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[600px]">
                <form onSubmit={handleSubmit}>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <MessageSquarePlus className="w-5 h-5" />
                            {t("support.createTicket.title")}
                        </DialogTitle>
                        <DialogDescription>
                            {t("support.createTicket.description")}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="subject">{t("support.createTicket.subject")} *</Label>
                            <Input
                                id="subject"
                                placeholder={t("support.createTicket.subjectPlaceholder")}
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                maxLength={200}
                                disabled={isCreatingTicket || isUploading}
                            />
                            <p className="text-xs text-muted-foreground">
                                {subject.length}/200 {t("support.createTicket.characters")}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="priority">{t("support.createTicket.priority")}</Label>
                            <Select
                                value={priority}
                                onValueChange={setPriority}
                                disabled={isCreatingTicket || isUploading}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder={t("support.createTicket.selectPriority")} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="LOW">{t("support.createTicket.priorityLow")}</SelectItem>
                                    <SelectItem value="NORMAL">{t("support.createTicket.priorityNormal")}</SelectItem>
                                    <SelectItem value="HIGH">{t("support.createTicket.priorityHigh")}</SelectItem>
                                    <SelectItem value="URGENT">{t("support.createTicket.priorityUrgent")}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="message">{t("support.createTicket.message")} *</Label>
                            <Textarea
                                id="message"
                                placeholder={t("support.createTicket.messagePlaceholder")}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                rows={6}
                                className="resize-none"
                                disabled={isCreatingTicket || isUploading}
                            />
                            <p className="text-xs text-muted-foreground">
                                {t("support.createTicket.messageHint")}
                            </p>
                        </div>

                        {/* File Upload Section */}
                        <div className="space-y-2">
                            <Label>{t("support.createTicket.attachments")}</Label>
                            <div className="border-2 border-dashed border-border/50 rounded-lg p-4">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    onChange={handleFileSelect}
                                    className="hidden"
                                    accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                                />

                                {/* Uploaded files preview */}
                                {uploadedFiles.length > 0 ? (
                                    <div className="space-y-2">
                                        {uploadedFiles.map((file) => {
                                            const FileIcon = getFileIcon(file.file?.type);
                                            const isImage = file.file?.type?.startsWith("image/");

                                            return (
                                                <div
                                                    key={file.id}
                                                    className="flex items-center gap-3 p-2 bg-muted/50 rounded-lg"
                                                >
                                                    {isImage && file.status === "completed" ? (
                                                        <img
                                                            src={file.permanentUrl || file.url || URL.createObjectURL(file.file)}
                                                            alt={file.name}
                                                            className="w-10 h-10 object-cover rounded"
                                                        />
                                                    ) : (
                                                        <div className="w-10 h-10 bg-white rounded flex items-center justify-center">
                                                            {file.status === "uploading" ? (
                                                                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                                                            ) : file.status === "error" ? (
                                                                <AlertCircle className="w-5 h-5 text-red-500" />
                                                            ) : (
                                                                <FileIcon className="w-5 h-5 text-muted-foreground" />
                                                            )}
                                                        </div>
                                                    )}
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium truncate">{file.name}</p>
                                                        <p className="text-xs text-muted-foreground">
                                                            {file.status === "uploading" && t("support.createTicket.uploading")}
                                                            {file.status === "completed" && t("support.createTicket.uploaded")}
                                                            {file.status === "error" && (file.error || t("support.createTicket.uploadError"))}
                                                        </p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeFile(file.id)}
                                                        className="p-1 hover:bg-gray-200 rounded"
                                                    >
                                                        <X className="w-4 h-4 text-muted-foreground" />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={isCreatingTicket || isUploading}
                                            className="w-full mt-2"
                                        >
                                            <Paperclip className="w-4 h-4 mr-2" />
                                            {t("support.createTicket.addMore")}
                                        </Button>
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={isCreatingTicket || isUploading}
                                        className="w-full py-4 flex flex-col items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        <Paperclip className="w-6 h-6" />
                                        <span className="text-sm">{t("support.createTicket.clickToUpload")}</span>
                                        <span className="text-xs">{t("support.createTicket.maxFileSize")}</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleCancel}
                            disabled={isCreatingTicket || isUploading}
                        >
                            {t("support.createTicket.cancel")}
                        </Button>
                        <Button
                            type="submit"
                            disabled={!subject.trim() || !message.trim() || isCreatingTicket || isUploading}
                            className="bg-gray-900 hover:bg-gray-800 text-white"
                        >
                            {isCreatingTicket ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    {t("support.createTicket.creating")}
                                </>
                            ) : (
                                t("support.createTicket.create")
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
